import React, { useEffect, useRef, useState } from "react";
import { contactsApi } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Check, Plus, UserPlus, X } from "lucide-react";
import { toast } from "sonner";

/**
 * ContactPicker — search + select or create-on-the-fly.
 * value: contact id (or empty)
 * onChange: (contact_id, contact?) => void
 */
export default function ContactPicker({ value, onChange, entityId, projectId }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [newContact, setNewContact] = useState({ full_name: "", email: "", phone: "", company: "", role: "" });
  const ref = useRef(null);

  useEffect(() => {
    if (value) {
      contactsApi.get(value).then(r => setSelected(r.data.contact)).catch(() => {});
    } else {
      setSelected(null);
    }
  }, [value]);

  useEffect(() => {
    if (!q || q.length < 1) { setResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const r = await contactsApi.list({ q });
        setResults(r.data);
      } catch {}
    }, 200);
    return () => clearTimeout(timer);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setShowResults(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pick = (c) => {
    setSelected(c);
    onChange(c.id, c);
    setQ("");
    setShowResults(false);
  };

  const startCreate = () => {
    setCreating(true);
    setNewContact({ full_name: q, email: "", phone: "", company: "", role: "" });
    setShowResults(false);
  };

  const createContact = async () => {
    if (!newContact.full_name.trim()) return toast.error("Name is required");
    try {
      const payload = {
        ...newContact,
        entity_ids: entityId ? [entityId] : [],
        project_ids: projectId ? [projectId] : [],
      };
      const r = await contactsApi.create(payload);
      if (r.data.duplicate) toast.info(`Existing contact matched: ${r.data.contact.full_name}`);
      else toast.success("Contact created");
      pick(r.data.contact);
      setCreating(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create contact");
    }
  };

  if (selected) {
    return (
      <div className="flex items-center justify-between gap-2 p-2.5 rounded-md bg-white/5 border border-white/10" data-testid="contact-picker-selected">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">{selected.full_name}</div>
          <div className="text-xs text-white/50">{selected.email || selected.role || selected.company || "Contact"}</div>
        </div>
        <button onClick={() => { setSelected(null); onChange(""); }} className="p-1 rounded hover:bg-white/10 text-white/60" data-testid="contact-picker-clear">
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  if (creating) {
    return (
      <div className="p-3 rounded-md bg-white/5 border border-white/10 space-y-2" data-testid="contact-picker-create">
        <div className="text-xs text-white/50 uppercase tracking-wider">New contact</div>
        <Input placeholder="Full name *" value={newContact.full_name} onChange={e=>setNewContact({...newContact, full_name: e.target.value})} className="bg-white/5 border-white/10" data-testid="new-contact-name"/>
        <Input placeholder="Email" value={newContact.email} onChange={e=>setNewContact({...newContact, email: e.target.value})} className="bg-white/5 border-white/10"/>
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Company" value={newContact.company} onChange={e=>setNewContact({...newContact, company: e.target.value})} className="bg-white/5 border-white/10"/>
          <Input placeholder="Role" value={newContact.role} onChange={e=>setNewContact({...newContact, role: e.target.value})} className="bg-white/5 border-white/10"/>
        </div>
        <Input placeholder="Phone" value={newContact.phone} onChange={e=>setNewContact({...newContact, phone: e.target.value})} className="bg-white/5 border-white/10"/>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm" onClick={()=>setCreating(false)} className="text-white/70 hover:text-white hover:bg-white/10">Cancel</Button>
          <Button size="sm" onClick={createContact} className="rounded-full bg-white text-black hover:bg-white/90" data-testid="new-contact-save"><Check className="w-3.5 h-3.5 mr-1"/>Create & assign</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <Input
        placeholder="Search contacts or type new name…"
        value={q}
        onChange={(e) => { setQ(e.target.value); setShowResults(true); }}
        onFocus={() => setShowResults(true)}
        className="bg-white/5 border-white/10"
        data-testid="contact-picker-input"
      />
      {showResults && (q.length > 0 || results.length > 0) && (
        <div className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto rounded-md border border-white/10 bg-[#1E2532] shadow-xl">
          {results.map(c => (
            <button
              key={c.id}
              type="button"
              onClick={() => pick(c)}
              className="w-full text-left p-2.5 hover:bg-white/10 border-b border-white/5 last:border-b-0"
              data-testid={`contact-picker-result-${c.id}`}
            >
              <div className="text-sm font-medium">{c.full_name}</div>
              <div className="text-xs text-white/50">{c.email || c.role || c.company || "—"}</div>
            </button>
          ))}
          {q.length > 0 && (
            <button
              type="button"
              onClick={startCreate}
              className="w-full text-left p-2.5 hover:bg-white/10 text-emerald-400 flex items-center gap-2"
              data-testid="contact-picker-create-btn"
            >
              <UserPlus className="w-4 h-4" /> Create "{q}"
            </button>
          )}
        </div>
      )}
    </div>
  );
}
